import { FooterButton } from "pro-features/components/MultiCategoriesChipDown/src/MultiCategoryChipdownStyles";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { i18nFilter } from "app/filters/i18nFilter";
import { ChipDownContainer, EllipsisDropdownItem } from "@similarweb/ui-components";
import { useWebsiteKeywordsPageTableTopContext } from "app/pages/website-analysis/traffic-sources/search/components/WebsiteKeywordsPageContext";
import {
    getSerpMapByKeys,
    SERP_IDS,
    SERP_MAP,
} from "app/pages/website-analysis/traffic-sources/search/components/serp/SerpTypes";
import styled from "styled-components";
import { colorsPalettes } from "@similarweb/styles";
import { useRankingDistributionTableTopContext } from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/useRankingDistributionTableTopContext";
import { allTrackers } from "app/services/track/track";

const EllipsisDropdownItemStyled = styled(EllipsisDropdownItem)`
    ${({ showCheckBox }) =>
        showCheckBox &&
        `
        display: flex;
    `}
    svg {
        path {
            fill: ${colorsPalettes.carbon[300]};
        }
    }
`;
// This is the representational component
export const SerpFilter = ({
    tableFilters,
    onSerpFilterApply,
    resetEnabled,
    availableSerpFeatures = SERP_MAP,
    tooltipText = null,
}) => {
    const initialSelected = useRef(tableFilters.serp || []);
    const dropdownRef = useRef<{ closePopup?: VoidFunction }>(null);
    const [selected, setSelected] = useState(initialSelected.current);
    useEffect(() => {
        if (tableFilters.serp) {
            setSelected(tableFilters.serp);
            initialSelected.current = tableFilters.serp;
        } else {
            setSelected([]);
            initialSelected.current = [];
        }
    }, [tableFilters.serp]);
    const onToggle = (isOpen) => {
        if (isOpen) {
            allTrackers.dispatchTrackEvent("Drop Down", "open", "Open SERP filter");
        } else {
            allTrackers.dispatchTrackEvent("Drop Down", "close", "Close SERP filter");
        }
    };
    const onClick = useCallback(
        ({ id }) => {
            allTrackers.dispatchTrackEvent("Drop Down", "click", `select SERP filter/${id || ""}`);
            const index = selected.indexOf(id);
            const newSelected = [...selected];
            if (index > -1) {
                newSelected.splice(index, 1);
            } else {
                newSelected.push(id);
            }
            setSelected(newSelected);
        },
        [selected],
    );
    const onClose = () => {
        dropdownRef?.current?.closePopup();
        // useTimeout here since we need to close the popup manually and then update the url.
        // if url is changed before the popup is closed, A react error is being thrown
        setTimeout(() => {
            onSerpFilterApply([]);
        });
        allTrackers.dispatchTrackEvent("Drop Down", "remove", "clear SERP filter");
    };
    const onApply = () => {
        dropdownRef?.current?.closePopup();
        // useTimeout here since we need to close the popup manually and then update the url.
        // if url is changed before the popup is closed, A react error is being thrown
        setTimeout(() => {
            onSerpFilterApply(selected);
        });
        allTrackers.dispatchTrackEvent("Drop Down", "submit-ok", `apply SERP filter/${selected.join(",") || ""}`);
    };
    const items = Object.values(availableSerpFeatures).map(({ id: serpId, icon }, index) => {
        const { name, id } = availableSerpFeatures[serpId];
        return (
            <EllipsisDropdownItemStyled
                key={index}
                id={id}
                showCheckBox={true}
                selected={false}
                iconName={icon}
                iconSize="sm"
            >
                {name}
            </EllipsisDropdownItemStyled>
        );
    });
    const selectedIds = selected.reduce((result, id) => {
        result[id] = true;
        return result;
    }, {});
    const selectedText = useMemo(() => {
        switch (selected.length) {
            case 0:
                return null;
            case 1:
                return i18nFilter()("serp.filter.selected.single.feature", {
                    name: SERP_MAP[selected[0]].name,
                });
            default:
                return i18nFilter()("serp.filter.selected.multiple.feature", {
                    name: SERP_MAP[selected[0]].name,
                    count: selected.length,
                });
        }
    }, [selected]);
    return (
        <div>
            <ChipDownContainer
                ref={dropdownRef}
                disabled={!resetEnabled}
                onToggle={onToggle}
                hasSearch={false}
                selectedIds={selectedIds}
                selectedText={selectedText}
                buttonText={i18nFilter()("serp.filter.placeholder")}
                onClick={onClick}
                onCloseItem={onClose}
                closeOnItemClick={false}
                width={300}
                footerComponent={() => (
                    <FooterButton onClick={onApply}>{i18nFilter()("serp.filter.apply")}</FooterButton>
                )}
                tooltipText={tooltipText}
                noResultsMessage={i18nFilter()("dropdown.no.results")}
            >
                {items}
            </ChipDownContainer>
        </div>
    );
};

// This are the "connectors" that connect the certain context to the representational component
const SerpFilterForWebsiteKeywords = () => {
    const { tableFilters, onSerpFilterApply, resetEnabled } = useWebsiteKeywordsPageTableTopContext();
    return (
        <SerpFilter
            tableFilters={tableFilters}
            onSerpFilterApply={onSerpFilterApply}
            resetEnabled={resetEnabled}
        />
    );
};

export const SerpFilterForRankingDistribution = () => {
    const { tableFilters, onSerpFilterApply } = useRankingDistributionTableTopContext();
    const availableSerpFeatures = getSerpMapByKeys(
        SERP_IDS.AI_OVERVIEW,
        SERP_IDS.ORGANIC_SITE_LINKS,
        SERP_IDS.NEWS,
        SERP_IDS.VIDEO,
        SERP_IDS.FEATURED_ANSWER,
        SERP_IDS.FEATURED_LIST,
        SERP_IDS.FEATURED_TABLE,
        SERP_IDS.TWITTER_FEED,
        SERP_IDS.PAID,
        SERP_IDS.PAID_SITE_LINKS,
        SERP_IDS.PRODUCT_LISTING_ADS,
    );
    return (
        <SerpFilter
            availableSerpFeatures={availableSerpFeatures}
            tableFilters={tableFilters}
            onSerpFilterApply={onSerpFilterApply}
            resetEnabled={true}
        />
    );
};
